/**
 * The AI-developer debug data of an answer (`done.debug`, backend plan
 * §4.3), turned into display strings for the debug panel (plan §3.8) and
 * the admin transcript viewer. Framework-free (copied to next-mapit as TS).
 *
 * The data comes from the server and is shown as is, so nothing here may
 * throw, and every value comes out as a plain string: a field of the wrong
 * type shows as `MISSING` instead of reaching React as an object (which
 * would crash the whole render).
 */

/** Shown for a missing or malformed value. */
export const MISSING = "—";

/** More chunks than this are cut (the backend sends the top few). */
export const MAX_CHUNKS = 50;

/** More flags than this are cut. */
export const MAX_FLAGS = 20;

const isRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** Latin digits with thousands separators, whatever the UI language. */
const groupDigits = (value) =>
  Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** @returns {string} a non-blank string or number as text, else MISSING */
export const formatText = (value) => {
  if (typeof value === "string") return value.trim() ? value : MISSING;
  return isNumber(value) ? String(value) : MISSING;
};

/** @returns {string} `"1,234 ms"` */
export const formatMs = (value) =>
  isNumber(value) && value >= 0 ? `${groupDigits(value)} ms` : MISSING;

/** @returns {string} `"1,234"` */
export const formatCount = (value) =>
  isNumber(value) && value >= 0 ? groupDigits(value) : MISSING;

/** @returns {string} `"$0.000412"` (costs per answer are tiny) */
export const formatCost = (value) =>
  isNumber(value) && value >= 0 ? `$${value.toFixed(6)}` : MISSING;

/** @returns {string} `"0.780"` */
export const formatScore = (value) =>
  isNumber(value) ? value.toFixed(3) : MISSING;

const formatTokens = (usage) => {
  const tokensIn = formatCount(usage.in);
  const tokensOut = formatCount(usage.out);
  if (tokensIn === MISSING && tokensOut === MISSING) return MISSING;
  return `${tokensIn} / ${tokensOut}`;
};

const toFlags = (flags) =>
  Array.isArray(flags)
    ? flags
        .map(formatText)
        .filter((flag) => flag !== MISSING)
        .slice(0, MAX_FLAGS)
    : [];

const toChunks = (chunks) =>
  Array.isArray(chunks)
    ? chunks
        .filter(isRecord)
        .slice(0, MAX_CHUNKS)
        .map((chunk) => ({
          key: formatText(chunk.key),
          title: formatText(chunk.title),
          file: formatText(chunk.file),
          score: formatScore(chunk.score),
        }))
    : [];

/**
 * @typedef {Object} DebugInfo
 * @property {string} profile
 * @property {string} provider
 * @property {string} model
 * @property {boolean | null} fallbackUsed null when unknown
 * @property {string} promptVersion
 * @property {string} indexVersion
 * @property {string} ttft e.g. `"812 ms"`
 * @property {string} total e.g. `"1,930 ms"`
 * @property {string} tokens `"in / out"`, e.g. `"912 / 340"`
 * @property {boolean} tokensEstimated the provider didn't report usage
 * @property {string} cost e.g. `"$0.000341"`
 * @property {string} retrievalMode `hybrid` | `lexical_only` | `none`
 * @property {string} topScore e.g. `"0.780"`
 * @property {string} condensedQuery
 * @property {Array<{ key: string, title: string, file: string, score: string }>} chunks
 * @property {string[]} flags
 */

/**
 * @param {unknown} debug `done.debug`: `{ profile, provider, model,
 *   fallbackUsed, promptVersion, indexVersion, latency: { ttftMs, totalMs },
 *   usage: { in, out, estimated, costUsd }, retrieval: { mode, topScore,
 *   condensedQuery, chunks: [{ key, title, file, score }] }, flags }`
 * @returns {DebugInfo | null} null when `debug` isn't an object
 */
export const toDebugInfo = (debug) => {
  if (!isRecord(debug)) return null;
  const latency = isRecord(debug.latency) ? debug.latency : {};
  const usage = isRecord(debug.usage) ? debug.usage : {};
  const retrieval = isRecord(debug.retrieval) ? debug.retrieval : {};

  return {
    profile: formatText(debug.profile),
    provider: formatText(debug.provider),
    model: formatText(debug.model),
    fallbackUsed:
      typeof debug.fallbackUsed === "boolean" ? debug.fallbackUsed : null,
    promptVersion: formatText(debug.promptVersion),
    indexVersion: formatText(debug.indexVersion),
    ttft: formatMs(latency.ttftMs),
    total: formatMs(latency.totalMs),
    tokens: formatTokens(usage),
    tokensEstimated: usage.estimated === true,
    cost: formatCost(usage.costUsd),
    retrievalMode: formatText(retrieval.mode),
    topScore: formatScore(retrieval.topScore),
    condensedQuery: formatText(retrieval.condensedQuery),
    chunks: toChunks(retrieval.chunks),
    flags: toFlags(debug.flags),
  };
};
